import { useState } from 'react'
import { ImageProps } from 'rebass'

import useTheme from '../../hooks/useTheme'

const BAD_SRCS: { [tokenAddress: string]: true } = {}

interface LogoProps extends Pick<ImageProps, 'style' | 'alt' | 'className'> {
  srcs: string[]
  needFallbackOnLoading?: boolean
  fallbackSrc?: string
}

/**
 * Renders an image by sequentially trying a list of URIs, and then eventually a fallback triangle alert
 */
export default function Logo({ srcs, needFallbackOnLoading = false, fallbackSrc, alt, style, ...rest }: LogoProps) {
  const [, refresh] = useState<number>(0)
  const [loaded, setLoaded] = useState<boolean>(needFallbackOnLoading ? false : true)
  const theme = useTheme()

  const src: string | undefined = srcs.find((src) => !BAD_SRCS[src])

  const handleLoad = () => setLoaded(true)

  const handleError = () => {
    if (src) BAD_SRCS[src] = true
    setLoaded(false)
    refresh((i) => i + 1)
  }

  const fallbackStyle = fallbackSrc
    ? {
        ...style,
        backgroundImage: `url("${fallbackSrc}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        border: `1px solid ${theme.deprecated_bg5}`,
      }
    : { ...style, color: theme.deprecated_bg4, borderRadius: 0 }

  if (!src) {
    return <div {...rest} style={fallbackStyle} />
  }

  if (loaded) {
    return <img {...rest} alt={alt} src={src} style={style} onError={handleError} />
  }

  return (
    <>
      <img alt="" src={src} style={{ display: 'none' }} onLoad={handleLoad} onError={handleError} />
      <div {...rest} style={fallbackStyle} />
    </>
  )
}
